import { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect, useDispatch, useSelector } from 'react-redux'
import { selectPinterestBoard, setPinterestProfileBoards } from 'services/compose'
import { activePostIdSelector } from 'services/compose/selectors'
import { getPinterestBoards } from 'services/destinations'
import { PinterestBoard } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { AlbumSelector } from './AlbumSelector'
import { PostDestination } from 'shared'

const FILTER_BOARDS_MIN_COUNT = 10

interface PinterestBoardSelectorOwnProps {
  profile: PostDestination
  boards?: Array<{
    id: string
    name: string
    url?: string
    selected?: boolean
  }>
  raised?: boolean
  className?: string
  onApplyToAll?: (b: PinterestBoard) => void
  onSelectedBoardChange?: (ppid: string, board: PinterestBoard) => void
}

interface PinterestBoardSelectorConnectedProps {
  fetchBoards: (profile: PostDestination) => Promise<any>
  setComposerPinterestBoard: (ppid: string, board: PinterestBoard) => void
}

type PinterestBoardSelectorProps = PinterestBoardSelectorOwnProps & PinterestBoardSelectorConnectedProps

export function PinterestBoardSelector(props: PinterestBoardSelectorProps) {
  const { fetchBoards } = props
  const dispatch = useDispatch()
  const editPostId = useSelector(activePostIdSelector)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // NOTE: the fulfilled reducer writes the boards back into the profile, so a fetch always hands us a new
  // profile reference. Without this guard an empty or failed response re-triggers the effect forever.
  const requestedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const requestKey = `${props.profile.ppid}:${editPostId}`
    const hasBoards = Object.keys(props.profile.pinterestBoards || {}).length > 0

    if (hasBoards || requestedKeyRef.current === requestKey) {
      return
    }

    requestedKeyRef.current = requestKey
    setLoading(true)
    setError(false)
    fetchBoards(props.profile)
      .then(response => {
        dispatch(setPinterestProfileBoards({ ppid: props.profile.ppid, boards: response.boards }))
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [props.profile, dispatch, fetchBoards, editPostId])

  const selectedBoard = props.boards && props.boards.find(b => b.selected)

  const onSelectedBoardChange = (a: PinterestBoard) => {
    if (props.onSelectedBoardChange) {
      props.onSelectedBoardChange(props.profile.ppid, a)
    } else {
      props.setComposerPinterestBoard(props.profile.ppid, a)
    }
  }

  const boardsMap = useMemo(() => {
    if (props.boards) {
      return props.boards.reduce((map: Record<string, any>, board: any) => {
        map[board.id] = board
        return map
      }, {})
    }

    return {}
  }, [props.boards])

  return (
    <AlbumSelector
      id="pinterest-board-selector"
      profile={props.profile}
      albums={boardsMap}
      selectedAlbumId={selectedBoard?.id}
      loading={loading}
      error={error}
      raised={props.raised}
      messageEmptySelection={<FormattedMessage id="composer.labels.empty-board" />}
      errorMessageElement={<FormattedMessage id="composer.labels.boards-fetch-failed" />}
      className={props.className}
      withFilter={Object.keys(boardsMap).length > FILTER_BOARDS_MIN_COUNT}
      onApplyToAll={props.onApplyToAll}
      onSelectionChange={onSelectedBoardChange}
    />
  )
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    fetchBoards: (profile: PostDestination) => dispatch(getPinterestBoards({ profile })).unwrap(),
    setComposerPinterestBoard: (ppid: string, board: PinterestBoard) => dispatch(selectPinterestBoard({ ppid, board }))
  }
}

export default connect(null, mapDispatchToProps)(PinterestBoardSelector)
